import { createTheme, alpha } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    surface: Palette['primary'];
  }
  interface PaletteOptions {
    surface?: PaletteOptions['primary'];
  }
}

// Palette Premium Energy (turquoise → vert du logo), structure Vuexy bordered
const primary = '#14b8a6';
const primaryDark = '#0f9e8e';
const accent = '#ff9f43';

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: primary, dark: primaryDark, light: '#5eead4', contrastText: '#ffffff' },
    secondary: { main: '#a8aaae', contrastText: '#ffffff' },
    error: { main: '#ea5455' },
    warning: { main: accent },
    success: { main: '#28c76f' },
    info: { main: '#00cfe8' },
    background: { default: '#f8f7fa', paper: '#ffffff' },
    text: { primary: '#444050', secondary: '#6d6b77' },
    divider: '#e6e6e8',
    surface: { main: '#f6f6f7', light: '#ffffff', dark: '#e6e6e8', contrastText: '#444050' },
  },
  typography: {
    fontFamily: '"Public Sans", "Inter", "Helvetica Neue", Arial, sans-serif',
    h1: { fontWeight: 700, letterSpacing: '-0.02em' },
    h2: { fontWeight: 700, letterSpacing: '-0.02em' },
    h3: { fontWeight: 700, letterSpacing: '-0.01em' },
    h4: {
      fontWeight: 800,
      letterSpacing: '-0.01em',
      fontSize: 'clamp(1.4rem, 1.1rem + 1vw, 1.875rem)',
    },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 500 },
    button: { fontWeight: 600, letterSpacing: 0 },
    body1: { fontSize: '0.9375rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', lineHeight: 1.55 },
  },
  shape: { borderRadius: 12 },
  shadows: [
    'none',
    '0 2px 8px rgba(47, 43, 61, 0.06)',
    '0 3px 12px rgba(47, 43, 61, 0.08)',
    '0 4px 18px rgba(47, 43, 61, 0.1)',
    '0 6px 24px rgba(47, 43, 61, 0.12)',
    '0 10px 30px rgba(47, 43, 61, 0.14)',
    ...(Array(19).fill('0 14px 40px rgba(47, 43, 61, 0.18)') as string[]),
  ] as never,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#f8f7fa',
        },
        ':focus-visible': {
          outline: `2px solid ${primary}`,
          outlineOffset: 2,
        },
        '::-webkit-scrollbar': { width: 10, height: 10 },
        '::-webkit-scrollbar-thumb': { background: '#d9d8dd', borderRadius: 8 },
        '::-webkit-scrollbar-thumb:hover': { background: '#b7b5be' },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backdropFilter: 'saturate(180%) blur(8px)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
          borderRight: 'none',
          boxShadow: '0 0 20px rgba(47, 43, 61, 0.06)',
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: ({ ownerState }) => ({
          backgroundImage: 'none',
          ...(ownerState.variant !== 'outlined' && {
            boxShadow: '0 2px 10px rgba(47, 43, 61, 0.08)',
            borderRadius: 12,
          }),
        }),
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          boxShadow: '0 2px 10px rgba(47, 43, 61, 0.08)',
          borderRadius: 14,
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 10,
          paddingTop: 8,
          paddingBottom: 8,
          paddingInline: 18,
        },
        containedPrimary: {
          boxShadow: '0 2px 6px rgba(20, 184, 166, 0.3)',
          '&:hover': {
            backgroundColor: primaryDark,
            boxShadow: '0 4px 12px rgba(20, 184, 166, 0.4)',
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined', fullWidth: true, size: 'small' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: '#ffffff',
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#c9c7d1' },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, borderRadius: 8 },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontWeight: 600,
            color: '#89868f',
            fontSize: 13,
            letterSpacing: 0,
            textTransform: 'none',
            borderBottom: '1px solid #eceaef',
            paddingTop: 14,
            paddingBottom: 14,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #f3f2f5',
          paddingTop: 14,
          paddingBottom: 14,
          fontSize: 14,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: alpha(primary, 0.03),
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          marginInline: 10,
          marginBlock: 2,
          color: '#444050',
          '& .MuiListItemIcon-root': {
            color: 'inherit',
            minWidth: 36,
          },
          '&:hover': { backgroundColor: alpha(primary, 0.06) },
          '&.active, &.Mui-selected': {
            background: `linear-gradient(72.47deg, ${primary} 22.16%, ${alpha(primary, 0.7)} 76.47%)`,
            color: '#ffffff',
            fontWeight: 600,
            boxShadow: '0 2px 6px rgba(20, 184, 166, 0.45)',
            '& .MuiListItemIcon-root': { color: '#ffffff' },
            '&:hover': {
              background: `linear-gradient(72.47deg, ${primaryDark} 22.16%, ${alpha(primaryDark, 0.75)} 76.47%)`,
            },
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#444050',
          fontSize: 12,
          padding: '6px 10px',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { height: 3, borderRadius: 3 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600, minHeight: 44 },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 10 },
      },
    },
  },
});
